import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Blankslate} from '@primer/react/experimental'
import {useState} from 'react'
import {getEntityCompositeID} from './entity-helpers'
import {Finding} from './Finding'
import {FindingsFilterBar} from './FindingsFilterBar'
import {FindingsSummary} from './FindingsSummary'
import pageStyles from './FindingsPage.module.css'
import type {FindingsPayload} from './types'
import {useFindingDismissals} from './use-finding-dismissals'

export const CopilotCodeReviewFindings: React.FC<FindingsPayload> = props => (
  <ErrorBoundary fallback={null}>
    <FindingsList {...props} />
  </ErrorBoundary>
)

export const FindingsList: React.FC<FindingsPayload> = ({
  findings,
  pullRequestBasePath,
  ignoredEntityIds: dismissedEntityIds,
  overviewSummary,
  mergeReadiness,
}) => {
  const [activeFilter, setActiveFilter] = useState<'open' | 'resolved'>('open')
  const {openFindings, resolvedFindings, handleDismiss, handleReopen} = useFindingDismissals(
    findings,
    pullRequestBasePath,
    dismissedEntityIds,
  )

  const displayedFindings = activeFilter === 'open' ? openFindings : resolvedFindings

  if (openFindings.length === 0 && resolvedFindings.length === 0) {
    return (
      <Blankslate>
        <Blankslate.Heading>No findings</Blankslate.Heading>
        <Blankslate.Description>There are no findings for this pull request.</Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <PageDataContextProvider basePageDataUrl={pullRequestBasePath}>
      <div className={pageStyles.PageLayout}>
        <FindingsSummary mergeReadiness={mergeReadiness} overviewSummary={overviewSummary} />
        <FindingsFilterBar
          openCount={openFindings.length}
          resolvedCount={resolvedFindings.length}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
        {displayedFindings.length === 0 && (
          <Blankslate>
            <Blankslate.Heading>
              {activeFilter === 'open' ? 'All findings resolved' : 'No resolved findings'}
            </Blankslate.Heading>
            <Blankslate.Description>
              {activeFilter === 'open'
                ? 'All findings for this pull request have been resolved.'
                : 'There are no resolved findings for this pull request.'}
            </Blankslate.Description>
          </Blankslate>
        )}
        <div>
          {activeFilter === 'open' &&
            displayedFindings.map(finding => (
              <Finding
                key={getEntityCompositeID(finding)}
                {...finding}
                basePath={pullRequestBasePath}
                onDismiss={handleDismiss}
              />
            ))}
          {activeFilter === 'resolved' &&
            displayedFindings.map(finding => (
              <Finding
                key={getEntityCompositeID(finding)}
                {...finding}
                basePath={pullRequestBasePath}
                dismissed
                onReopen={handleReopen}
              />
            ))}
        </div>
      </div>
    </PageDataContextProvider>
  )
}
